import { DOMElement } from "react";
import classNames from "classnames";
import murmur from "murmurhash-js";
import { HIGHLIGHTING_COLORS, MATH_CHARS } from "./constants";
import { GoToPageWithHighlight, Highlight, Language, StartEnd } from "./types";

const $: JQueryStatic = (window as any).jQuery;

const parseSentenceAndReplace = (opts: {
  element: Node;
  page: string | undefined;
  lang?: Language;
}): void => {
  const regExp = new RegExp(
    `(([«(“'"]{0,}([0-9${MATH_CHARS}.<>,-]+)[\n ,.?!:)»”]{0,})|([«(“'"]*([a-zA-Z0-9&;#_ñáéíóúü¿¡ÑÁÉÍÓÚÜ=\u0301]+)[\n ,?.;:!/)»”–’—"_*-]{0,})(?![^<>&;]*[>]))`,
    "g",
  );

  const html =
    opts?.element.nodeValue?.replace(
      regExp,
      `<span class="${classNames("highlight-wrapper", {
        notranslate: opts.lang === "es",
      })}" data-page="${opts?.page}">$1</span>`,
    ) || "";

  // replace text node with highlightable html
  const replacementNode = document.createElement("span");
  replacementNode.classList.add("highlighting-sentence-wrapper");
  replacementNode.innerHTML = html;
  if (opts?.element.parentNode) {
    opts?.element.parentNode.insertBefore(replacementNode, opts?.element);
    opts?.element.parentNode.removeChild(opts?.element);
  }
};

export const applyCoordinates = (): void => {
  $(".highlight-wrapper").each((key, item) => {
    $(item).attr("data-hash", murmur.murmur3($(item).text()));
    $(item).attr("data-sequence", key + 1);
  });
};

const getPage = (element: HTMLElement): string => {
  const elementPart = $(element).parents("#elementPart");
  const assessmentQuestion = $(element).parents("#assessmentQuestion");

  let pageOfElement = "1";

  if (elementPart.length) {
    pageOfElement = elementPart.get(0).getAttribute("data-page") || "1";
  }
  if (assessmentQuestion.length) {
    pageOfElement = assessmentQuestion.get(0).getAttribute("data-page") || "1";
  }

  if (pageOfElement === null) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    pageOfElement = params?.questionPage || params["page[number]"];
  }

  return pageOfElement || "1";
};

export const wrapWords = (lang?: Language) => (): void => {
  const elements = document.querySelectorAll('[data-highlightable="true"]');
  elements.forEach((element) => {
    const pageOfElement = getPage(element as HTMLElement);

    const nodeIterator = document.createNodeIterator(
      element,
      NodeFilter.SHOW_TEXT,
      null,
    );
    let node;

    // eslint-disable-next-line no-cond-assign
    while ((node = nodeIterator.nextNode())) {
      if (
        !$(node).parents('.katex, figcaption, [data-highlightable="false"]')
          .length &&
        node.nodeValue?.trim()
      ) {
        parseSentenceAndReplace({ element: node, page: pageOfElement, lang });
      }
    }
  });
};

const resolveSequence = (data: StartEnd): number | null => {
  // Here trying to find by hash unique value
  const byHashItems = $(`.highlight-wrapper[data-hash="${data.hash}"]`);
  if (byHashItems.length === 0) {
    return null;
  }
  if (byHashItems.length === 1) {
    return Number($(byHashItems[0]).attr("data-sequence"));
  }

  // Here trying to find by siblings: prev and next;
  const filteredItems: JQuery[] = [];
  byHashItems.each((key, item) => {
    const currentSequence = parseInt($(item).attr("data-sequence") || "");
    let prevItem;
    let nextItem;
    if (!data.isFirst) {
      prevItem = $(
        `.highlight-wrapper[data-sequence="${currentSequence - 1}"]`,
      );
    }
    if (!data.isLast) {
      nextItem = $(
        `.highlight-wrapper[data-sequence="${currentSequence + 1}"]`,
      );
    }
    if (
      prevItem !== undefined &&
      prevItem.attr("data-hash") === data.prevHash &&
      nextItem !== undefined &&
      nextItem.attr("data-hash") === data.nextHash
    ) {
      filteredItems.push($(item));
    }
  });
  if (filteredItems.length === 1) {
    return Number($(filteredItems[0]).attr("data-sequence"));
  }

  // Here trying to resolve sequence by index in collection
  if (byHashItems.length === parseInt(data.allWithHashCount as string)) {
    return Number(
      $(byHashItems[data.allWithHashIndex as number]).attr("data-sequence"),
    );
  }

  return null;
};

export const getHighlightedElements = (selection: Highlight): JQuery | null => {
  const startSequence = resolveSequence(selection.start);
  const endSequence = resolveSequence(selection.end);
  const selectors = [];

  if (!(startSequence && endSequence)) {
    return null;
  }

  for (let i = startSequence; i <= endSequence; i += 1) {
    selectors.push(`.highlight-wrapper[data-sequence="${i}"]`);
  }

  if (selectors.length) {
    return $(selectors.join(", "));
  }

  return null;
};

export const paintElements = (
  $elements: JQuery,
  color: string | null,
  readonly: boolean,
): void => {
  $elements
    .removeClass(Object.keys(HIGHLIGHTING_COLORS).join(" "))
    .addClass(`highlighted ${color || ""} ${readonly ? "readonly" : ""}`);
};

export const isMobileDevice = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
    navigator.userAgent,
  ) ||
  (/Mac/.test(navigator.userAgent) && !!navigator.maxTouchPoints);

export const detectSelection = (
  $element: JQuery,
  selections: Highlight[],
): Highlight | undefined => {
  const sequence = parseInt($element.data("sequence"));
  return (
    selections.find(
      (selection) =>
        sequence >= selection.start.sequence &&
        sequence <= selection.end.sequence &&
        $element.hasClass(String(selection.color)) &&
        !selection.readonly,
    ) ||
    selections.find(
      (selection) =>
        sequence >= selection.start.sequence &&
        sequence <= selection.end.sequence &&
        $element.hasClass(String(selection.color)) &&
        selection.readonly,
    )
  );
};

export const checkIfSelectionFirst = ($obj: JQuery): boolean => {
  return parseInt($obj.attr("data-sequence") || "") === 0;
};

export const checkIfSelectionLast = ($obj: JQuery): boolean => {
  return (
    Number($obj.attr("data-sequence")) === $(".highlight-wrapper").length - 1
  );
};
export const getPreviousHash = ($obj: JQuery): string | undefined | null => {
  if (checkIfSelectionFirst($obj)) {
    return null;
  }

  return $(
    `.highlight-wrapper[data-sequence='${
      parseInt($obj.attr("data-sequence") || "") - 1
    }']`,
  ).attr("data-hash");
};

export const getNextHash = ($obj: JQuery): string | undefined | null => {
  if (checkIfSelectionLast($obj)) {
    return null;
  }

  return $(
    `.highlight-wrapper[data-sequence='${
      parseInt($obj.attr("data-sequence") || "") + 1
    }']`,
  ).attr("data-hash");
};

export const getIndexOfElementInCollection = (
  element: JQuery,
  collection: JQuery,
): number | null => {
  let index = null;
  $.each(collection, (key, item) => {
    if (element[0] === item) {
      index = key;
    }
  });

  return index;
};

export const getSelectionObject = ({
  startAtPoint,
  endAtPoint,
  text,
}: {
  startAtPoint: JQuery;
  endAtPoint: JQuery;
  text: string;
}): Highlight => {
  const $startAtSelection = $(startAtPoint);
  const $endAtSelection = $(endAtPoint);
  const elementsWithStartHash = $(
    `.highlight-wrapper[data-hash="${$startAtSelection.attr("data-hash")}"]`,
  );
  const elementsWithEndHash = $(
    `.highlight-wrapper[data-hash="${$endAtSelection.attr("data-hash")}"]`,
  );

  return {
    comment: null,
    commentCreated: null,
    createdAt: "",
    id: undefined,
    name: "",
    readonly: false,
    source: "",
    studentAssignmentId: 0,
    studentId: 0,
    updatedAt: null,
    selectedItems:
      parseInt($endAtSelection.attr("data-sequence") || "") -
      parseInt($startAtSelection.attr("data-sequence") || "") +
      1,
    start: {
      sequence: parseInt($startAtSelection.attr("data-sequence") || ""),
      isFirst: checkIfSelectionFirst($startAtSelection),
      isLast: checkIfSelectionLast($startAtSelection),
      hash: $startAtSelection.attr("data-hash"),
      prevHash: getPreviousHash($startAtSelection),
      nextHash: getNextHash($startAtSelection),
      allWithHashCount: elementsWithStartHash.length,
      allWithHashIndex: getIndexOfElementInCollection(
        $startAtSelection,
        elementsWithStartHash,
      ),
    },
    end: {
      sequence: parseInt($endAtSelection.attr("data-sequence") || ""),
      isFirst: checkIfSelectionFirst($endAtSelection),
      isLast: checkIfSelectionLast($endAtSelection),
      hash: $endAtSelection.attr("data-hash"),
      prevHash: getPreviousHash($endAtSelection),
      nextHash: getNextHash($endAtSelection),
      allWithHashCount: elementsWithEndHash.length,
      allWithHashIndex: getIndexOfElementInCollection(
        $endAtSelection,
        elementsWithEndHash,
      ),
    },
    highlightedText: text,
    color: "lightblue",
  };
};

export const removeSelectionRange = (): void => {
  const selection = window.getSelection();
  if (selection) {
    if (selection?.empty) {
      // Chrome
      selection.empty();
    } else if (selection.removeAllRanges) {
      // Firefox
      selection.removeAllRanges();
    }
  }
};

export const redrawSelection = (selection: Highlight): void => {
  const $elements = getHighlightedElements(selection);
  if ($elements) {
    $elements
      .removeClass(Object.keys(HIGHLIGHTING_COLORS).join(" "))
      .addClass(`highlighted ${selection.color || ""}`);
  }
};

export const removeSelection = (selection: Highlight): void => {
  const $elements = getHighlightedElements(selection);
  if ($elements) {
    $elements.removeClass(
      `${Object.keys(HIGHLIGHTING_COLORS).join(" ")} highlighted`,
    );
  }
};

export const scrollToSelection = (sequence: number | string): void => {
  setTimeout(() => {
    $("html, body").animate(
      {
        scrollTop:
          ($(`.highlighted[data-sequence="${sequence}"]`).offset()?.top || 0) -
          130,
      },
      1000,
    );
  }, 0);
};

export const goToPageWithHighlight: GoToPageWithHighlight = ({
  sequence,
  params,
  setParams,
}): void => {
  const currentPage =
    params.get("questionPage") || params.get("page.number") || "1";

  const pageOfHighlight = $(`.highlighted[data-sequence="${sequence}"]`).attr(
    "data-page",
  );

  if (currentPage !== pageOfHighlight) {
    if (params?.page?.number) {
      return setParams({
        ...params,
        page: { ...params.page, number: Number(pageOfHighlight) },
      });
    }
    setParams({ questionPage: pageOfHighlight });
  }
};

export const getCommentIcon = (selection: Highlight): Element | null => {
  let icon = document.querySelector(`[data-highlight-id="${selection.id}"]`);
  if (!icon) {
    const $elements = getHighlightedElements(selection);
    if ($elements) {
      const $prev = $($elements.prev().get(0));
      if ($prev.hasClass("highlight-comment-icon")) {
        icon = $prev.get(0);
      }
    }
  }
  return icon;
};

export const addCommentIcon = (selection: Highlight): Element | null => {
  const commentIcon = getCommentIcon(selection);

  if (commentIcon) {
    const id = (commentIcon as HTMLElement).dataset.highlightId;
    if (selection.id && id === "undefined") {
      (commentIcon as HTMLElement).dataset.highlightId = selection.id as string;
    }
    return commentIcon;
  }
  const $elements = getHighlightedElements(selection);
  if ($elements) {
    const firstElement = $elements.get(0);
    const icon = document.createElement("span");
    icon.className = "highlight-comment-icon";
    icon.dataset.highlightId = String(selection.id);
    icon.innerHTML =
      '<svg aria-hidden="true" focusable="false" class="Icon_svg__2ig9Q Icon_medium__2sBrf"><use xlink:href="#note-filled"></use></svg>';
    if (firstElement.parentNode) {
      return firstElement.parentNode.insertBefore(icon, firstElement);
    }
  }

  return null;
};

export const removeCommentIcon = (selection: Highlight): void => {
  const commentIcon = getCommentIcon(selection);

  if (commentIcon) {
    commentIcon.remove();
  }
};

export const setTextToClipboard = (text: string): void => {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.left = "-10000px";
  el.style.top = "-10000px";
  el.textContent = text;
  document.body.appendChild(el);
  el.contentEditable = "true";
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
  }
  document.body.removeChild(el);
};

export const scrollToElement = (el: DOMElement<any, any>): void => {
  $("html, body").animate(
    {
      scrollTop: ($(el).offset()?.top || 0) - 160,
    },
    1000,
  );
};

export const hasSeveralComments = (
  selections: Highlight[],
  selection: Highlight | null,
): boolean =>
  selections.filter(
    (v: Highlight) => v.start.hash === selection?.start.hash && v.comment,
  ).length > 1;

export const getDuplicated = (
  startAtPoint: HTMLElement | null,
  endAtPoint: HTMLElement | null,
  selections: Highlight[],
): Highlight | undefined => {
  const startHash = startAtPoint?.dataset.hash;
  const endHash = endAtPoint?.dataset.hash;
  return selections.find(
    (selection) =>
      selection.start.hash === startHash &&
      selection.end.hash === endHash &&
      startAtPoint?.classList.contains(String(selection.color)) &&
      !selection.readonly,
  );
};
